import { useState, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Truck, TreeDeciduous, Droplets, Flame, Sparkles, Volume2, VolumeX } from 'lucide-react';

// Asset URLs
const VIDEO_URL = 'https://blossom.ditto.pub/82f7d1aa3deb2a93f5a4cfffd0865e181ab71920cddb89ec1361bc457380459c.mp4';
const LOGO_URL = 'https://blossom.ditto.pub/0691ec701454a2e37b178e09caf429088e623c351b28ffed34c588a9531a62e3.png';
const TAP_IMAGE = 'https://blossom.dreamith.to/85198a8914615e2bda2ea8024cde42ecee468f643f5c8114f9bf80d3d569c78e.jpeg';
const COLLECT_IMAGE = 'https://blossom.ditto.pub/75431256acdb92c5610ddfcd30c50ba42d80f297f5fa8cd2e3d485fcd6e9cdbe.jpeg';
const BOIL_IMAGE = 'https://blossom.dreamith.to/aab9f64b8dd3397558aa4a4723518675fe7412d09c653742e2178d2f0918f756.jpeg';
const SYRUP_IMAGE = 'https://blossom.ditto.pub/74b073ee1db7009844f18ec79d47580d52584fe0f13350056a8ddffc4d047389.png';
const SYRUP_IMAGE_ORIGINAL = 'https://blossom.dreamith.to/b2418e72b823b4555a73f9edd8aedb91222e991819aba7e86c9a6358fc177cdd.jpeg';

const Index = () => {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useSeoMeta({
    title: 'MN Maple Ridge | 100% Pure Minnesota Maple Syrup',
    description: 'From our family to yours! 100% pure maple syrup made on our family farm in Alexandria, MN. Three generations working together each spring.',
  });

  const scrollToProduct = () => {
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section with Video */}
      <section className="bg-gradient-to-b from-amber-50 to-white">
        <div className="container max-w-6xl px-4 py-4 md:py-6">
          {/* Mobile: Logo above video */}
          <div className="lg:hidden text-center mb-4">
            <img 
              src={LOGO_URL} 
              alt="MN Maple Ridge LLC Logo" 
              className="h-16 mx-auto"
            />
          </div>

          {/* Desktop: Side by side layout */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8">
            {/* Video Container */}
            <div className="lg:w-3/5 mb-4 lg:mb-0">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200">
                <video
                  ref={videoRef}
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
                
                {/* Sound Toggle Button */}
                <button
                  onClick={toggleMute}
                  className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                  aria-label={isMuted ? 'Turn sound on' : 'Turn sound off'}
                >
                  {isMuted ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Hero Text & CTA */}
            <div className="lg:w-2/5 text-center">
              {/* Desktop: Logo in sidebar */}
              <img 
                src={LOGO_URL} 
                alt="MN Maple Ridge LLC Logo" 
                className="h-20 mx-auto mb-4 hidden lg:block"
              />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-amber-900 mb-2">
                100% Pure Maple Syrup
              </h1>
              <p className="text-lg md:text-xl text-amber-700 mb-1">
                Made in Alexandria, Minnesota
              </p>
              <p className="text-base md:text-lg text-amber-600 mb-4 italic">
                "From our Family to Yours"
              </p>
              <Button 
                onClick={scrollToProduct}
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white text-base md:text-lg px-6 py-5 rounded-full animate-pulse-glow shadow-xl transition-all duration-300 hover:scale-105"
              >
                Buy Maple Syrup
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section id="product" className="py-16 md:py-24 bg-amber-50">
        <div className="container max-w-5xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-amber-900">
              Our Maple Syrup
            </h2>
          </div>

          {/* Product Image */}
          <div className="max-w-md mx-auto mb-10">
            <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-amber-200">
              <img 
                src={SYRUP_IMAGE} 
                alt="Kids holding bottles of MN Maple Ridge pure maple syrup"
                className="w-full aspect-square object-cover"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="text-center mb-8 max-w-2xl mx-auto">
            <p className="text-amber-800 text-lg leading-relaxed">
              Hi! We're Mack and Mara. We've helped make maple syrup for years, and this year we decided to build a website and sell it ourselves! We even vibe coded it (with a little help 😄). We tap trees, carry sap, and keep the boil going every spring. We hope you like our syrup!
            </p>
          </div>

          {/* Size Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
            {/* 8 oz */}
            <Card className="border-2 border-amber-200 hover:border-amber-400 transition-all duration-300 hover:shadow-lg bg-white">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-amber-600 mb-1">$12</div>
                <div className="text-amber-800 font-medium">8 oz</div>
                <div className="text-amber-600 text-sm mt-1">Small</div>
                <Button 
                  asChild
                  className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <a 
                    href="https://pay.zaprite.com/pl_6f0fWTIwLA" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Buy 8 oz
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* 12 oz */}
            <Card className="border-2 border-amber-200 hover:border-amber-400 transition-all duration-300 hover:shadow-lg bg-white">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-amber-600 mb-1">$15</div>
                <div className="text-amber-800 font-medium">12 oz</div>
                <div className="text-amber-600 text-sm mt-1">Medium</div>
                <Button 
                  asChild
                  className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <a 
                    href="https://pay.zaprite.com/pl_86g41gEMzm" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Buy 12 oz
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* 32 oz */}
            <Card className="border-2 border-amber-200 hover:border-amber-400 transition-all duration-300 hover:shadow-lg bg-white">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-amber-600 mb-1">$35</div>
                <div className="text-amber-800 font-medium">32 oz</div>
                <div className="text-amber-600 text-sm mt-1">Large</div>
                <Button 
                  asChild
                  className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <a 
                    href="https://pay.zaprite.com/pl_6h4szVPXOH" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Buy 32 oz
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-sm text-amber-600 text-center">
            Accepts credit cards, debit, & Bitcoin
          </p>
        </div>
      </section>

      {/* How It's Made Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container max-w-6xl px-4">
          <div className="text-center mb-12">
            <img 
              src={LOGO_URL} 
              alt="MN Maple Ridge" 
              className="h-12 mx-auto mb-4"
            />
            <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mb-4">
How We Make It
            </h2>
            <p className="text-amber-700 text-lg">
              From tree to table, the way it should be done!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1: Tap */}
            <Card className="overflow-hidden border-2 border-amber-100 hover:border-amber-300 transition-all duration-300 hover:shadow-xl group">
              <CardContent className="p-0">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={TAP_IMAGE} 
                    alt="Kids tapping maple trees"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 text-center bg-gradient-to-b from-white to-amber-50">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TreeDeciduous className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-amber-900 text-lg">1. Tap</h3>
                  <p className="text-amber-600 text-sm mt-1">We drill holes in the maple trees</p>
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Collect */}
            <Card className="overflow-hidden border-2 border-amber-100 hover:border-amber-300 transition-all duration-300 hover:shadow-xl group">
              <CardContent className="p-0">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={COLLECT_IMAGE} 
                    alt="Kids collecting maple sap in buckets"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 text-center bg-gradient-to-b from-white to-amber-50">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Droplets className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-amber-900 text-lg">2. Collect</h3>
                  <p className="text-amber-600 text-sm mt-1">We gather the sap from the trees</p>
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Boil */}
            <Card className="overflow-hidden border-2 border-amber-100 hover:border-amber-300 transition-all duration-300 hover:shadow-xl group">
              <CardContent className="p-0">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={BOIL_IMAGE} 
                    alt="Dad and son boiling maple sap in the sugar shack"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 text-center bg-gradient-to-b from-white to-amber-50">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Flame className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-amber-900 text-lg">3. Boil</h3>
                  <p className="text-amber-600 text-sm mt-1">We boil it down in our sugar shack</p>
                </div>
              </CardContent>
            </Card>

            {/* Step 4: Syrup */}
            <Card className="overflow-hidden border-2 border-amber-100 hover:border-amber-300 transition-all duration-300 hover:shadow-xl group">
              <CardContent className="p-0">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={SYRUP_IMAGE_ORIGINAL} 
                    alt="Kids holding finished maple syrup bottles"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 text-center bg-gradient-to-b from-white to-amber-50">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-amber-900 text-lg">4. Syrup!</h3>
                  <p className="text-amber-600 text-sm mt-1">Delicious pure maple syrup!</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pickup Info Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-amber-600 to-amber-700 text-white">
        <div className="container max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-8">
            <Truck className="w-5 h-5" />
            <span className="font-medium">Pickup & Delivery</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Local Pickup & Delivery Only
          </h2>
          
          <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-xl mx-auto">
            <p className="text-xl leading-relaxed mb-6">
              Right now we're delivering locally (with help from dad 😄), so no shipping yet. We'll contact you after your order!
            </p>
            <div className="flex items-center justify-center gap-2 text-amber-200">
              <MapPin className="w-5 h-5" />
              <span>Alexandria, Minnesota</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-amber-900 text-amber-100">
        <div className="container max-w-4xl px-4 text-center">
          <img 
            src={LOGO_URL} 
            alt="MN Maple Ridge Logo" 
            className="h-16 mx-auto mb-4 brightness-0 invert opacity-80"
          />
          <p className="text-amber-200 mb-4">Alexandria, MN</p>
          <p className="text-amber-300 italic mb-8">"From our Family to Yours"</p>
          
          <div className="border-t border-amber-800 pt-8 text-sm text-amber-400">
            <p>© {new Date().getFullYear()} MN Maple Ridge LLC. All rights reserved.</p>
            <p className="mt-2">
              Vibed with{' '}
              <a 
                href="https://shakespeare.diy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-300 hover:text-white transition-colors underline"
              >
                Shakespeare
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
